import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Detect whether a URL points at a video asset. Used by the #GOYAGIRLS
 * gallery (admin editor + storefront tile grid) so images and short reels
 * can live in the same list. Cloudinary auto-transformed URLs preserve
 * the source extension, so this works both for our uploaded videos and
 * direct-paste embeds. Falls back to false when unsure so we default to
 * <img> — never break a photo tile just because a URL is ambiguous.
 */
// Admin-set framing per product image; defaults to top so heads are never cropped.
export const imgPos = (p, url) => p?.image_positions?.[url] || "50% 0%";

export const VIDEO_EXTENSIONS = ["mp4", "webm", "mov", "m4v", "ogv"];
export const isVideoUrl = (url) => {
  if (typeof url !== "string" || !url) return false;
  const clean = url.split("?")[0].split("#")[0].toLowerCase();
  return VIDEO_EXTENSIONS.some((ext) => clean.endsWith(`.${ext}`));
};

/**
 * CDN image helpers. Two CDN backends live behind these functions:
 *
 *   • Bunny.net (primary, since Feb 2026) — URLs look like
 *     `https://cdn.goyalisboa.pt/products/<uuid>/original.<ext>` with
 *     three pre-generated WebP variants alongside the original:
 *     `w400.webp`, `w800.webp`, `w1200.webp`. We pick the variant
 *     closest to the requested render width.
 *
 *   • Cloudinary (legacy, still live for un-migrated assets) — uses
 *     Cloudinary's on-the-fly transform pipeline:
 *       f_auto        → serve WebP/AVIF on browsers that support them
 *       q_auto:eco    → aggressive perceptual compression
 *       c_limit       → downscale only, never upscale
 *       w_<n>         → target width in CSS px
 *       dpr_auto      → responsive per-device pixel ratio
 *
 * Non-CDN URLs (local `/items/*.jpg`, `/api/files/*`, absolute URLs
 * to other hosts) are returned unchanged. If we can't confidently
 * rewrite, we do nothing — never risk a 404 or a broken image over a
 * byte saving.
 */
const CLOUDINARY_HOST = "res.cloudinary.com";

// Structural match for a Bunny.net upload emitted by `bunny.upload_image`:
// `<any-cdn>/<prefix>/<asset-id>/original.<ext>`. Matching by shape (not
// by domain) keeps the helper working if the pull-zone URL ever changes.
const BUNNY_ORIGINAL_RE = /\/([^/]+)\/([a-f0-9]{16,})\/original\.([a-z0-9]{2,5})(?:$|[?#])/i;
const BUNNY_VARIANT_RE = /\/([^/]+)\/([a-f0-9]{16,})\/w\d+\.webp(?:$|[?#])/i;

const isBunnyUrl = (src) =>
  typeof src === "string" && (BUNNY_ORIGINAL_RE.test(src) || BUNNY_VARIANT_RE.test(src));

// Widths our backend pre-generates for every Bunny upload.
const BUNNY_VARIANT_WIDTHS = [400, 800, 1200];

const _bunnyBasePath = (src) => {
  // Returns everything up to and including `<asset-id>/` so we can
  // append the variant filename. Strips any query/hash first.
  const clean = src.split("?")[0].split("#")[0];
  const m = clean.match(/^(.*\/[a-f0-9]{16,}\/)(original\.[a-z0-9]{2,5}|w\d+\.webp)$/i);
  return m ? m[1] : null;
};

const _pickBunnyWidth = (targetWidth) => {
  const t = Math.round(targetWidth * (typeof window !== "undefined" && window.devicePixelRatio > 1 ? 1.5 : 1));
  // First width ≥ target, otherwise the largest we have.
  return BUNNY_VARIANT_WIDTHS.find((w) => w >= t) || BUNNY_VARIANT_WIDTHS[BUNNY_VARIANT_WIDTHS.length - 1];
};

export const cldImage = (src, width = 800) => {
  if (typeof src !== "string" || !src) return src;
  if (isVideoUrl(src)) return src;                       // videos need their own pipeline

  // Bunny.net — swap `original.<ext>` for the closest `wNNN.webp` variant.
  if (isBunnyUrl(src)) {
    const base = _bunnyBasePath(src);
    if (!base) return src;
    return `${base}w${_pickBunnyWidth(width)}.webp`;
  }

  // Cloudinary — inject transforms into the URL.
  if (src.includes(CLOUDINARY_HOST)) {
    const parts = src.split("/upload/");
    if (parts.length !== 2) return src;
    // Guard against double-transformation (already contains f_auto etc.)
    if (/(^|\/)(f_auto|q_auto|w_\d+)/.test(parts[1])) return src;
    const w = Math.max(120, Math.min(Math.round(width), 1800));
    const transforms = `f_auto,q_auto:eco,c_limit,dpr_auto,w_${w}`;
    return `${parts[0]}/upload/${transforms}/${parts[1]}`;
  }

  return src;
};

/**
 * Build a `srcset` string for responsive `<img>` tags. Emits three
 * widths so the browser picks the smallest that satisfies the layout's
 * CSS width × device pixel ratio.
 */
export const cldSrcSet = (src, widths = [400, 800, 1200]) => {
  if (typeof src !== "string" || !src) return undefined;
  if (isVideoUrl(src)) return undefined;

  // Bunny — build the srcset off the pre-generated variants.
  if (isBunnyUrl(src)) {
    const base = _bunnyBasePath(src);
    if (!base) return undefined;
    // Restrict `widths` to those we actually have variants for, and
    // always sort ascending so the browser sees them in a canonical order.
    const supported = widths
      .map((w) => BUNNY_VARIANT_WIDTHS.find((v) => v >= w) || BUNNY_VARIANT_WIDTHS[BUNNY_VARIANT_WIDTHS.length - 1])
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .sort((a, b) => a - b);
    return supported.map((w) => `${base}w${w}.webp ${w}w`).join(", ");
  }

  if (src.includes(CLOUDINARY_HOST)) {
    return widths.map((w) => `${cldImage(src, w)} ${w}w`).join(", ");
  }

  return undefined;
};

/**
 * Low-quality image placeholder (LQIP).
 *
 * Returns a URL for a ~32 px wide, heavily-blurred variant of `src`
 * that renders near-instantly (usually <1 KB) so it can sit as a
 * background layer while the sharp photo streams in on top.
 *
 * Two paths:
 *   • Cloudinary URLs → use Cloudinary's own on-the-fly transform
 *     (`f_auto,q_1,e_blur:1000,w_40,c_limit`). Delivered from their
 *     edge CDN so first byte is typically <150 ms.
 *   • Everything else (local `/items/*`, admin uploads under
 *     `/api/files/*`, absolute URLs) → our backend `/api/lqip?src=...`
 *     endpoint. It fetches the source once, generates a ~500 B blurred
 *     JPEG, and caches both in-memory (backend) and via the
 *     `Cache-Control: immutable, 1-year` header (browser).
 *
 * For local `/items/*` we short-circuit to the pre-generated
 * `/items/lqip/*.jpg` file to skip even the backend roundtrip.
 *
 * Returns `null` only when we truly can't produce a placeholder (e.g.
 * empty string).
 */
export const cldLqip = (src) => {
  if (typeof src !== "string" || !src) return null;
  if (isVideoUrl(src)) return null;

  // Bunny.net — the smallest pre-generated variant (400 px WebP) is
  // already tiny (~10-20 KB), served from an edge CDN. Use it as the
  // placeholder rather than round-tripping through our backend LQIP
  // endpoint. Not as tiny as a true 32-px blurred JPEG, but the CDN
  // hit is fast enough that the perceived load feels instant and we
  // avoid backend load for every image on every page.
  if (isBunnyUrl(src)) {
    const base = _bunnyBasePath(src);
    if (!base) return null;
    return `${base}w${BUNNY_VARIANT_WIDTHS[0]}.webp`;
  }

  // Cloudinary — use their transform pipeline.
  if (src.includes(CLOUDINARY_HOST)) {
    const parts = src.split("/upload/");
    if (parts.length !== 2) return null;
    let tail = parts[1];
    const versionIdx = tail.search(/\/v\d+\//);
    if (versionIdx >= 0) tail = tail.slice(versionIdx + 1);
    return `${parts[0]}/upload/f_auto,q_1,e_blur:1000,w_40,c_limit/${tail}`;
  }

  // Local static assets — pre-generated .jpg files under /items/lqip/.
  // Strips the source extension (.jpeg / .webp / .png) so we always
  // hit the JPEG placeholder.
  if (src.startsWith("/items/") && !src.startsWith("/items/lqip/")) {
    const rest = src.slice("/items/".length);
    const dot = rest.lastIndexOf(".");
    const base = dot > 0 ? rest.slice(0, dot) : rest;
    return `/items/lqip/${base}.jpg`;
  }

  // Everything else → backend endpoint. `src` is passed through the
  // query string; the backend handles absolute URLs, `/api/files/*`,
  // and unrecognised paths.
  return `/api/lqip?src=${encodeURIComponent(src)}`;
};
